<template>
  <NoticeDialog
    :open="warningVisible"
    variant="warning"
    mode="acknowledge"
    :title="t('session.idleTitle')"
    :confirm-label="t('session.idleStay')"
    @confirm="staySignedIn"
    @close="staySignedIn"
  >
    {{ t('session.idleBody') }}
    <!-- The countdown stands alone rather than sitting inside the sentence: a
         number that has to agree with a noun ("1 second" / "2 seconds") is nine
         different plural rules, and Polish alone has three forms. `role="timer"`
         with the default aria-live="off" announces it on demand instead of
         interrupting a screen reader once a second. -->
    <p class="idle-countdown" role="timer">{{ countdown }}</p>
  </NoticeDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NoticeDialog } from '@lumi/ui';
import { formatIdleCountdown, useIdleLogout } from '../composables/useIdleLogout';

// One tag at the app root wires the suite-wide idle timeout (issue 265) into an
// app:
//
//   <IdleLogoutGuard :authenticated="Boolean(auth.token)" @timeout="signOut" />
//
// The app keeps what only it knows — how it logs out and where its login lives.
// `timeout` fires exactly once. Everything else (activity, cross-tab stamp, the
// countdown) lives in useIdleLogout.
const props = withDefaults(
  defineProps<{
    /** Whether there is a session to time out. Reactive; no side effects. */
    authenticated: boolean;
    /** Overridable for tests; production never passes these. */
    timeoutMs?: number;
    warningMs?: number;
  }>(),
  { timeoutMs: undefined, warningMs: undefined },
);

const emit = defineEmits<{ timeout: [] }>();

const { t } = useI18n();

const { warningVisible, secondsRemaining, staySignedIn } = useIdleLogout({
  isAuthenticated: () => props.authenticated,
  onTimeout: () => emit('timeout'),
  timeoutMs: props.timeoutMs,
  warningMs: props.warningMs,
});

const countdown = computed(() => formatIdleCountdown(secondsRemaining.value));
</script>

<style scoped>
.idle-countdown {
  margin-top: var(--space-3);
  font-size: var(--text-2xl);
  font-variant-numeric: tabular-nums;
  font-weight: var(--weight-bold);
  color: var(--text-heading);
}
</style>
